import { Component, ErrorInfo, ReactNode } from 'react';
// @mui
import { Box, Button, Stack, Typography } from '@mui/material';

// ----------------------------------------------------------------------

// After a new deploy the old lazy page chunks are gone, so the page reloads once to fetch the new ones
const CHUNK_RELOAD_KEY = 'chunkReloadAt';

const isChunkLoadError = (error: any) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '') ||
  /Failed to fetch dynamically imported module/i.test(error?.message || '');

const reloadOnceForChunk = () => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    // A reload in the last 10 seconds did not help, so show the error instead of looping
    if (Date.now() - last < 10000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

type Props = {
  children: ReactNode;
  // When this changes (e.g. the route), a caught error is cleared
  resetKey?: string;
  fullPage?: boolean;
};

type State = {
  error: Error | null;
  resetKey?: string;
};

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page crashed:', error, info?.componentStack);
    if (isChunkLoadError(error)) reloadOnceForChunk();
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children, fullPage } = this.props;

    if (!error) return children;

    const chunkError = isChunkLoadError(error);

    return (
      <Box
        sx={{
          px: 3,
          py: 8,
          textAlign: 'center',
          ...(fullPage && { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, mx: 'auto' }}>
          <Typography variant="h5">
            {chunkError ? 'A new version is available' : 'Something went wrong on this page'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {chunkError
              ? 'Please reload the page to get the latest version.'
              : 'This section could not be shown. You can try again, or open another page from the menu.'}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            {!chunkError && (
              <Button variant="outlined" onClick={this.handleRetry}>
                Try Again
              </Button>
            )}
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
